import { useMemo, useState } from 'react';

import Loader from '@/components/Loader';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useGetDisciplines } from '@/hooks/queries/useDisciplines';
import { useGetDivisions } from '@/hooks/queries/useDivisions';
import { useReportCourses } from '@/hooks/queries/useReportCourses';
import { useCourseCount } from '@/hooks/queries/useStats';
import { useTabLabels } from '@/hooks/useTabLabels';

import CatalogReport from './CatalogReport';
import MaterialsReport from './MaterialsReport';
import OutcomesReport from './OutcomesReport';
import ReadinessReport from './ReadinessReport';
import ReportFiltersBar from './ReportFilters';
import { emptyReportFilters, filterReportCourses, type ReportFilters } from './reportUtils';

const schoolId = import.meta.env.VITE_SCHOOL_ID as string;

const tabContentClassName = 'mt-0 overflow-visible p-0 flex-none min-h-0 focus-visible:ring-0';

const Reports = () => {
	const [activeTab, setActiveTab] = useState('materials');
	const [filters, setFilters] = useState<ReportFilters>(emptyReportFilters);
	const labels = useTabLabels();

	const { data: courses, isLoading: isLoadingCourses } = useReportCourses();
	const { data: disciplines, isLoading: isLoadingDisciplines } = useGetDisciplines();
	const { data: divisions, isLoading: isLoadingDivisions } = useGetDivisions();
	const { data: courseCount, isLoading: isLoadingCourseCount } = useCourseCount();

	const isLoading =
		isLoadingCourses || isLoadingDisciplines || isLoadingDivisions || isLoadingCourseCount;

	const filteredCourses = useMemo(
		() => filterReportCourses(courses ?? [], filters),
		[courses, filters]
	);

	const unmappedCourseIds = useMemo(
		() => new Set((courseCount?.unusedCourses ?? []).map((c) => c.course_id)),
		[courseCount?.unusedCourses]
	);

	const tabDescriptions: Record<string, string> = {
		materials: `Inventory of textbooks and other ${labels.resources.toLowerCase()}, listed by grade and discipline.`,
		catalog: 'Snapshot of courses with grade, division, owners, AP status, and last updated.',
		outcomes: `${labels.outcomes} across courses, grouped by grade and discipline.`,
		readiness: `Where curriculum documentation is incomplete: missing ${labels.description.toLowerCase()}, ${labels.outcomes.toLowerCase()}, ${labels.resources.toLowerCase()}, owners, or map placement.`,
	};

	if (isLoading) return <Loader />;

	return (
		<div className="flex flex-col gap-4 pb-6">
			<Tabs value={activeTab} onValueChange={setActiveTab} className="flex flex-col gap-4">
				<div className="flex flex-col gap-2">
					<TabsList variant="line" className="w-full shrink-0">
						<TabsTrigger variant="line" value="materials">
							{labels.resources}
						</TabsTrigger>
						<TabsTrigger variant="line" value="catalog">
							Catalog
						</TabsTrigger>
						<TabsTrigger variant="line" value="outcomes">
							{labels.outcomes}
						</TabsTrigger>
						<TabsTrigger variant="line" value="readiness">
							Readiness
						</TabsTrigger>
					</TabsList>
					<p className="text-sm text-(--header-text)">{tabDescriptions[activeTab]}</p>
				</div>

				<div className="bg-(--card-bg-color) rounded-[6px] shadow-[0px_2px_4px_1px_rgba(0,15,25,0.05),0px_0px_4px_0_rgba(0,15,25,0.05)] p-(--spacing-4)">
					<ReportFiltersBar
						filters={filters}
						onChange={setFilters}
						disciplines={disciplines ?? []}
						divisions={divisions ?? []}
					/>
				</div>

				<div className="bg-(--card-bg-color) rounded-[6px] shadow-[0px_2px_4px_1px_rgba(0,15,25,0.05),0px_0px_4px_0_rgba(0,15,25,0.05)] p-(--spacing-4)">
					<TabsContent value="materials" className={tabContentClassName}>
						<MaterialsReport courses={filteredCourses} schoolId={schoolId} />
					</TabsContent>
					<TabsContent value="catalog" className={tabContentClassName}>
						<CatalogReport courses={filteredCourses} schoolId={schoolId} />
					</TabsContent>
					<TabsContent value="outcomes" className={tabContentClassName}>
						<OutcomesReport courses={filteredCourses} schoolId={schoolId} />
					</TabsContent>
					<TabsContent value="readiness" className={tabContentClassName}>
						<ReadinessReport
							courses={filteredCourses}
							unmappedCourseIds={unmappedCourseIds}
							schoolId={schoolId}
						/>
					</TabsContent>
				</div>
			</Tabs>
		</div>
	);
};

export default Reports;
